import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomPaginationTable from '../../components/table/CustomPaginationTable';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import AddUploadForm from '../../components/customForm/AddUploadForm';
import Button from '../../components/button';
import AddIcon from '../../static/image/addIcon.svg';
import { RED, YELLOW, BLACK } from '../../utils/constant';
import ExcelSelectedShopUpload from './ExcelSelectedShopUpload';
import {
  getListSelectShopData,
  updateDigitalPriceTagData,
  fetchAllShopList
} from '../../redux/digitalPriceSlice';
import { formatDatetime } from '../../utils/dateUtils';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';
import { decodedJWTPayload } from '../../utils/commonUtil';

function UploadExcelSelectedShop() {
  const dispatch = useDispatch();
  const {
    selectShopData: {
      resdata,
      totalElements,
      error,
      loading,
    },
    shopList,
    shopListLoading,
    shopListError
  } = useSelector((state) => state.digitalPrice);

  const [pagination, setPagination] = useState({
    page: 0,
    size: 10
  });

  const [searchParams, setSearchParams] = useState({
    sku: '',
    promoDate: null,
    selectedSingtel: [],
    selectedShop: []
  });

  const [shouldSearch, setShouldSearch] = useState(false);
  const [isAddUploadOpen, setIsAddUploadOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [shouldDisableUpload, setShouldDisableUpload] = useState(false);
  const [shouldDisableDelete, setShouldDisableDelete] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      const payload = decodedJWTPayload(token);
      const mnId = payload?.mn_id;

      if (mnId) {
        const match = mnId.match(/\|040100-([RW]+)\|/);
        if (match) {
          const permission = match[1];
          if (permission === 'R') {
            setShouldDisableUpload(true);
            setShouldDisableDelete(true);
          } else if (permission === 'RW') {
            setShouldDisableDelete(true);
            setShouldDisableUpload(false);
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    const initData = async () => {
      await dispatch(fetchAllShopList());
      setShouldSearch(true);
    };
    initData();
  }, [dispatch]);

  useEffect(() => {
    if (shouldSearch) {
      const formattedSearchParams = {
        ...searchParams,
        promoDate: searchParams.promoDate ? formatDate(searchParams.promoDate) : null
      };

      dispatch(getListSelectShopData({
        ...pagination,
        ...formattedSearchParams
      }));

      setShouldSearch(false);
    }
  }, [dispatch, pagination, searchParams, shouldSearch]);

  const shopOptions = React.useMemo(() => {
    if (!shopList || !Array.isArray(shopList)) return { singtel: [], serShop: [] };

    return {
      singtel: shopList
        .filter(shop => shop.isActive === 1 && shop.shopCategory === 'SINGTEL')
        .map(shop => ({
          value: shop.shopId,
          label: shop.shopName,
        })),
      serShop: shopList
        .filter(shop => shop.isActive === 1 && shop.shopCategory === 'PARTNER')
        .map(shop => ({
          value: shop.shopId,
          label: shop.shopName,
        })),
    };
  }, [shopList]);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedRow) {
      const payload = {
        isActive: selectedRow.isActive,
        discountDataId: selectedRow.discountDataId,
        discountType: 3
      };
      dispatch(updateDigitalPriceTagData(payload))
        .unwrap()
        .then(() => {
          const formattedSearchParams = {
            ...searchParams,
            promoDate: searchParams.promoDate ? formatDate(searchParams.promoDate) : null
          };
          dispatch(getListSelectShopData({
            ...pagination,
            ...formattedSearchParams
          }));
        });
    }
    setShowDeleteModal(false);
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      page: page - 1
    }));
    setShouldSearch(true);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPagination({
      page: page - 1,
      size: newPerPage
    });
    setShouldSearch(true);
  };

  const handleSearchParamsChange = (key, value) => {
    setSearchParams(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = () => {
    setShouldSearch(true);
  };

  const handleReset = () => {
    setSearchParams({
      sku: '',
      promoDate: null,
      selectedSingtel: [],
      selectedShop: []
    });
    setShouldSearch(true);
  };

  const handleUploadClick = () => {
    setIsAddUploadOpen(true);
  };

  const handleBackClick = () => {
    setIsAddUploadOpen(false);
    setShouldSearch(true);
  };

  const getRowClass = (row) => {
    if (row.isError) return "error-msg";
    if (!row.isError && row.messageText) return "error-msg-orange";
    return "non-error-msg";
  };

  const columns = [
    {
      name: <div className="break-words">Record ID</div>,
      cell: (row) => <div className={getRowClass(row)}>{row.discountDataId}</div>,
    },
    {
      name: <div className="break-words">Stock Code</div>,
      cell: (row) => <div className={getRowClass(row)}>{row.stockCode}</div>,
    },
    {
      name: <div className="break-words">Brand Name</div>,
      cell: (row) => <div className={getRowClass(row)}>{row.brandName}</div>,
    },
    {
      name: <div className="break-words">Model Name</div>,
      cell: (row) => <div className={getRowClass(row)}>{row.modelName}</div>,
    },
    {
      name: 'Discount',
      cell: (row, index) => (
        <div className={getRowClass(row)}>
          {row.discountAmount !== null && row.discountAmount !== undefined
            ? parseFloat(row.discountAmount).toFixed(2)
            : ''}
        </div>
      ),
    },
    {
      name: <div>Promo RRP</div>,
      cell: (row, index) => (
        <div className={getRowClass(row)}>
          {row.promoRrp !== null && row.promoRrp !== undefined
            ? parseFloat(row.promoRrp).toFixed(2)
            : ''}
        </div>
      ),
    },
    {
      name: <div className="break-words">Promo Start Date</div>,
      cell: (row) => <div className={getRowClass(row)}>
        {row.periodStart ? row.periodStart.substring(0, 10) : null}
      </div>,
    },
    {
      name: <div className="break-words">Promo End Date</div>,
      cell: (row) => <div className={getRowClass(row)}>
        {row.periodEnd ? row.periodEnd.substring(0, 10) : null}
      </div>,
    },
    {
      name: <div className="break-words">Selected Singtel Shop(s)</div>,
      cell: (row) => <div className={getRowClass(row)}>{row.singtelShopNames}</div>,
    },
    {
      name: <div className="break-words">Selected SER Shop(s)</div>,
      cell: (row) => <div className={getRowClass(row)}>{row.partnerShopNames}</div>,
    },
    {
      name: <div className="break-words">Uploaded By</div>,
      cell: (row) => <div className={getRowClass(row)}>{row.createdBy}</div>,
    },
    {
      name: <div className="break-words">Uploaded Date Time</div>,
      cell: (row) => <div className={getRowClass(row)}>{formatDatetime(row.updatedDatetime)}</div>,
    },
    {
      name: <div className="break-words">Message</div>,
      cell: (row) => <div className={getRowClass(row)}>{row.messageText || row.message}</div>,
    },
    {
      name: <div className="break-words">Actions</div>,
      cell: (row) => (
        <div style={{ display: 'flex', gap: '10px', cursor: shouldDisableDelete ? 'default' : 'pointer' }}>
          <img
            src={DeleteIcon}
            alt="Delete"
            onClick={() => handleDeleteClick(row)}
            style={{ opacity: shouldDisableDelete ? 0.5 : 1, pointerEvents: shouldDisableDelete ? 'none' : 'auto' }}
          />
        </div>
      ),
      sortable: false,
    },
  ];

  return (
    <div className="manage-user-container">
      {!isAddUploadOpen ? (
        <div style={{ padding: '30px' }}>
          Upload Excel (Selected Shop)

          <div style={{ display: 'flex', marginTop: '20px' }}>
            <AddUploadForm
              onSearchParamsChange={handleSearchParamsChange}
              searchParams={searchParams}
              showSelects={true}
              singtelOptions={shopOptions.singtel}
              shopOptions={shopOptions.serShop}
            />

            <div style={{ marginLeft: 'auto', display: 'flex', marginTop: '35px' }}>
              <Button className='upload-btn' style={{ marginRight: '20px' }} onClick={handleSearch}>
                SEARCH
              </Button>
              <Button className='upload-btn' onClick={handleReset}>
                RESET
              </Button>
            </div>
          </div>

          <div style={{ display: 'flex', marginTop: '20px', justifyContent: 'space-between' }}>
            <div>
              <Button
                className='upload-add-request-btn'
                onClick={handleUploadClick}
                style={{ opacity: shouldDisableUpload ? 0.5 : 1, pointerEvents: shouldDisableUpload ? 'none' : 'auto' }}
              >
                <img src={AddIcon} style={{ marginRight: '5px' }} alt="Add" />
                UPLOAD DISCOUNTED RRP EXCEL
              </Button>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              fontSize: '12px',
              gap: '4px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#ED1A3D',
                  marginRight: '8px'
                }} />
                <div>{RED}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#FFA500',
                  marginRight: '8px'
                }} />
                <div>{YELLOW}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#2C2C2C',
                  marginRight: '8px'
                }} />
                <div>{BLACK}</div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <CustomPaginationTable
              columns={columns}
              data={resdata || []}
              loading={loading}
              totalRows={totalElements}
              currentPage={pagination.page + 1}
              perPage={pagination.size}
              onPageChange={handlePageChange}
              onPerRowsChange={handlePerRowsChange}
            />
          </div>
        </div>
      ) : (
        <ExcelSelectedShopUpload
          onBackClick={handleBackClick}
          singtelOptions={shopOptions.singtel}
          shopOptions={shopOptions.serShop}
        />
      )}

      <CommonDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        itemDetails={selectedRow}
      />
    </div>
  );
}

export default UploadExcelSelectedShop;